import { useState, useCallback } from "react";
import { Check, Copy, WrapText, PanelRightOpen } from "lucide-react";
import { Highlight, themes } from "prism-react-renderer";

const CodeBlock = ({ language, code, onOpenInEditor }) => {
  const [copied, setCopied] = useState(false);
  const [wordWrap, setWordWrap] = useState(false);

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(code).catch(console.error);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [code]);

  const lines = code.split("\n");
  const showLineNumbers = lines.length >= 4;
  const lineNumWidth = String(lines.length).length;

  const normalizeLanguage = (lang) => {
    const aliases = {
      js: "javascript",
      ts: "typescript",
      tsx: "tsx",
      jsx: "jsx",
      py: "python",
      rb: "ruby",
      sh: "bash",
      shell: "bash",
      zsh: "bash",
      yml: "yaml",
      md: "markdown",
      cs: "csharp",
      "c++": "cpp",
      "c#": "csharp",
      objc: "objectivec",
      kt: "kotlin",
      rs: "rust",
      tf: "hcl",
      dockerfile: "docker",
    };
    const normalized = (lang || "").toLowerCase().trim();
    return aliases[normalized] || normalized;
  };

  const lang = normalizeLanguage(language);
  const displayLang = lang || "code";

  return (
    <div className="my-3 rounded-lg overflow-hidden bg-[#011627] ring-1 ring-surfaceBorder">
      <div className="flex items-center justify-between px-4 py-2 bg-surface/40 border-b border-surfaceBorder">
        <span className="text-[11px] font-light text-mutedText uppercase tracking-wider select-none">
          {displayLang}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setWordWrap((w) => !w)}
            title={wordWrap ? "Disable word wrap" : "Enable word wrap"}
            className={[
              "inline-flex items-center gap-1 rounded px-1.5 py-1 text-[11px] transition-colors cursor-pointer",
              wordWrap
                ? "text-mainText bg-surface/80"
                : "text-mutedText hover:text-mainText",
            ].join(" ")}
          >
            <WrapText className="h-3 w-3" strokeWidth={2} />
          </button>
          {onOpenInEditor && (
            <button
              onClick={() => onOpenInEditor({ language: lang, code })}
              title="Open in Editor"
              className="inline-flex items-center gap-1.5 rounded px-2 py-1 text-[11px] font-light text-mutedText hover:text-mainText transition-colors cursor-pointer"
            >
              <PanelRightOpen className="h-3 w-3" strokeWidth={2} />
              <span>Editor</span>
            </button>
          )}
          <button
            onClick={handleCopy}
            className="inline-flex items-center gap-1.5 rounded px-2 py-1 text-[11px] font-light text-mutedText hover:text-mainText transition-colors cursor-pointer"
          >
            {copied ? (
              <>
                <Check className="h-3 w-3 text-activeCase" strokeWidth={2} />
                <span className="text-activeCase">Copied</span>
              </>
            ) : (
              <>
                <Copy className="h-3 w-3" strokeWidth={2} />
                <span>Copy</span>
              </>
            )}
          </button>
        </div>
      </div>

      <Highlight theme={themes.nightOwl} code={code} language={lang || "text"}>
        {({ className, style, tokens, getLineProps, getTokenProps }) => (
          <pre
            className={[
              "overflow-x-auto p-4 text-[13px] leading-relaxed font-mono",
              wordWrap ? "whitespace-pre-wrap break-words" : "",
            ].join(" ")}
            style={{ ...style, background: "transparent", margin: 0 }}
          >
            {tokens.map((line, i) => {
              const lineProps = getLineProps({ line, key: i });
              return (
                <div
                  key={i}
                  {...lineProps}
                  className={[lineProps.className || "", "table-row"].join(" ")}
                >
                  {showLineNumbers && (
                    <span
                      className="table-cell pr-4 text-right select-none text-mutedText/40"
                      style={{ minWidth: `${lineNumWidth + 1}ch` }}
                    >
                      {i + 1}
                    </span>
                  )}
                  <span className="table-cell">
                    {line.map((token, j) => (
                      <span key={j} {...getTokenProps({ token, key: j })} />
                    ))}
                  </span>
                </div>
              );
            })}
          </pre>
        )}
      </Highlight>
    </div>
  );
};

export default CodeBlock;
